import { useTranslation } from 'react-i18next';
import useU2000AxiosAuthenticated from '../../u2000Core/axios/U2000UseAxiosAuthenticated';

import U2000BasicTable, { type U2000SearchPayload, type U2000SearchResult } from '../../u2000Core/components/tables/basic/U2000BasicTable';
import { U2000IdColumnDef, type U2000TableColumn } from '../../u2000Core/components/tables/basic/U2000BasicTableList';
import React, { useEffect, useRef } from 'react';
import { useToast } from '../../u2000Core/context/toast/U2000ToastContext';
import { U2000HandleApiError } from '../../u2000Core/axios/U2000HandleApiError';
import { useU2000SearchHandlers } from '../../u2000Core/hooks/handler/U2000UseSearchHandlers';
import { useU2000QuickSearchListEffect } from '../../u2000Core/hooks/useU2000QuickSearchListEffect';
import { useNavigate } from 'react-router-dom';
import type { TFunction } from 'i18next';

import type { U2000TableHandlers } from '../../u2000Core/types/U2000TableHandlers';
import { U2000GridStateEnumPaginationDirection } from '../../u2000Core/reducer/formReducer/U2000GridState';
import { U2000LUStateEnumNextAction } from '../../u2000Core/reducer/formReducer/U2000LUState';
import { useU2000DeleteHandlers } from '../../u2000Core/hooks/handler/U2000UseDeleteHandlers';
import { U2000GridStateDeleteAction, type U2000GridStateNextPageAfterDeleteReturn } from '../../u2000Core/reducer/formReducer/U2000GridStateHandlers';
import { useU3157Context } from '../U3157Context';
import { doDeleteData, doSearch, luHandleAppiError, type U3157ImportModelSearchRow } from '../U3157Service';
import { NS } from '../U3157';
import U3157WorkNatureImportModelDeleteDlg from './common/U3157WorkNatureImportModelDeleteDlg';
import { U3157WorkNatureImportModelListToolBar } from './list/U3157WorkNatureImportModelListToolBar';
import { useU2000LogicalUnitContext } from '../../u2000Core/context/logicalUnit/U2000LogicalUnitContext';
import { u2000BuildQuickSearchFormPath, u2000ShouldQuickSearchOpenForm } from '../../u2000Core/tools/U2000QuickSearchUtils';

function getColumnsDef(t: TFunction): U2000TableColumn[] {
  return [
    U2000IdColumnDef,
    {
      name: 'name',
      label: t('lblMandateTypeCode', { ns: NS }),
      widthClass: 'w-[160px]',
      type: 'string',
      align: 'left',
      isCriteriaField: true,
      inGridField: true,
      isDefaultColumn: true,
      sortable: true,
      isDefaultSortedColumn: true,
    },
    {
      name: 'workNatureCount',
      label: t('lblWorkNatureCount', { ns: NS }),
      widthClass: 'w-[140px]',
      type: 'number',
      align: 'right',
      isCriteriaField: true,
      inGridField: true,
      isDefaultColumn: true,
      sortable: true,
      isDefaultSortedColumn: false,
    },
    {
      name: 'createdAt',
      label: t('lblFldCreatedAt', { ns: NS }),
      widthClass: 'w-[150px]',
      isDefaultColumn: true,
      type: 'date',
      inGridField: true,
      sortable: true,
      includeConditions: ['equals', 'notequals', 'greaterthan', 'greaterthanorequal', 'lesthan', 'lesthanorequal', 'interval'],
      isCriteriaField: true,
    },
    {
      name: 'deletedAt',
      label: t('lblCndFieldDeletedAt', { ns: NS }),
      widthClass: 'w-[250px]',
      type: 'date',
      inGridField: true,
      align: 'center',
      display: (_column, value) => {
        return value ? (
          <span className="font-bold text-red-600">{t('lblRowSoftDeleted', { ns: NS })}</span>
        ) : (
          ''
        );
      },
      includeConditions: ['isNotNull'],
      ovverideLabelConditions: [
        {
          name: 'isNotNull',
          label: t('lblCndDeletedAt', { ns: NS }),
        },
      ],
      isCriteriaField: true,
    },
  ];
}

const U3157WorkNatureImportModelList: React.FC = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const toast = useToast();

  const { luStateHandlers, searchStateHandlers, gridStateHandlers } = useU3157Context();
  const u2000AxiosAuthenticated = useU2000AxiosAuthenticated();
  const { setLogicalUnitBreadcrumb, quickSearchData, setQuickSearchData } = useU2000LogicalUnitContext();
  const consumeQsSessionRef = useRef<() => { isFromQuickSearch: boolean; targetLogicalUnit: string } | null>(() => null);

  useEffect(() => {
    const listTitle = t('title', { ns: NS });
    searchStateHandlers.setColumns(getColumnsDef(t));
    gridStateHandlers.setColumns(getColumnsDef(t));
    setLogicalUnitBreadcrumb([{ label: listTitle }]);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- handlers instables
  }, [t, i18n.language, i18n.resolvedLanguage]);

  const handleSearch = useU2000SearchHandlers<U3157ImportModelSearchRow>({
    doSearchFn: async (searchPayload: U2000SearchPayload) => {
      return await doSearch(u2000AxiosAuthenticated, searchPayload);
    },
    onSuccess: (searchResult: U2000SearchResult<U3157ImportModelSearchRow>, direction: U2000GridStateEnumPaginationDirection) => {
      gridStateHandlers.initGridAfterSearch(searchResult, direction);
      searchStateHandlers.keepFilterAfterSearch(searchStateHandlers.extractSearchParameter());

      const qsSession = consumeQsSessionRef.current();
      const isFromQuickSearch = qsSession?.isFromQuickSearch === true;
      const targetLU = qsSession?.targetLogicalUnit ?? '/U3157';
      if (
        u2000ShouldQuickSearchOpenForm(window.location.pathname, targetLU) &&
        searchResult.data &&
        searchResult.data.length === 1 &&
        isFromQuickSearch
      ) {
        const single = searchResult.data[0];
        luStateHandlers.setNextAction(U2000LUStateEnumNextAction.NONE);
        luStateHandlers.setActionOriginFrom('');
        const currentIdx = gridStateHandlers.getData().findIndex((r) => r.id === single.id);
        if (currentIdx !== -1) {
          gridStateHandlers.setCurrentIdx(currentIdx);
        }
        navigate(u2000BuildQuickSearchFormPath(targetLU, single.id), { replace: true });
      }
    },
    onError: ({ error }: { error: unknown }) => {
      const errorResult = U2000HandleApiError(error, t, NS, luHandleAppiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
    },
  });

  const handleDelete = useU2000DeleteHandlers({
    getSelectedRows: () => (gridStateHandlers ? gridStateHandlers.getSelectedRows() : []),
    doDeleteFn: async (id, row) => {
      return await doDeleteData(u2000AxiosAuthenticated, id, { id: row.id, version: row.version });
    },

    onSuccess: (deletedRows) => {
      const page = gridStateHandlers.getCurrentPage();
      let newData = gridStateHandlers.getData();
      let totalRows = gridStateHandlers.getTotalRows();

      deletedRows.forEach((row) => {
        toast.success(t('success0003', { key: row.name ?? row.id, ns: NS }), t('successTitle', { ns: NS }));
        newData = newData.filter((dataRow) => dataRow.id !== row?.id);
        totalRows -= 1;
      });
      gridStateHandlers.setData(newData);
      gridStateHandlers.setTotalRows(totalRows);

      const action: U2000GridStateNextPageAfterDeleteReturn = gridStateHandlers.getNextPageAfterDelete(page, totalRows);
      if (action.action === U2000GridStateDeleteAction.PAGENEXT && action.page) {
        handleSearch.handleSearch(
          searchStateHandlers.extractSearchParameter(),
          gridStateHandlers.extractPagination(action.page),
          U2000GridStateEnumPaginationDirection.NEXTPAGE,
        );
      }
    },

    onError: (errors) => {
      errors.forEach(({ error }) => {
        const errorResult = U2000HandleApiError(error, t, NS, luHandleAppiError);
        if (errorResult.genericErrors?.length) {
          errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
        }
      });
    },
  });

  const u3157ListHandlerActions: U2000TableHandlers<U3157ImportModelSearchRow> = {
    onSearch: (page: number, direction: U2000GridStateEnumPaginationDirection) => {
      handleSearch.handleSearch(searchStateHandlers.extractSearchParameter(), gridStateHandlers.extractPagination(page), direction);
    },
    onRowClick: (row: U3157ImportModelSearchRow) => {
      luStateHandlers.setNextAction(U2000LUStateEnumNextAction.NONE);
      luStateHandlers.setActionOriginFrom('');
      const currentIdx = gridStateHandlers.getData().findIndex((r) => r.id === row.id);
      gridStateHandlers.setCurrentIdx(currentIdx);
      navigate(`${row.id}`);
    },
    onAdd: () => {
      luStateHandlers.setNextAction(U2000LUStateEnumNextAction.ADD);
      luStateHandlers.setActionOriginFrom('/U3157');
      navigate('new');
    },
    onModify: () => {
      luStateHandlers.setNextAction(U2000LUStateEnumNextAction.MODIFY);
      luStateHandlers.setActionOriginFrom('/U3157');
      navigate(`${gridStateHandlers.getSelectedRows()[0]?.id}`);
    },
    onDelete: () => {
      handleDelete.handleDelete();
    },
  };

  const { consumeQuickSearchSession } = useU2000QuickSearchListEffect({
    targetLogicalUnit: '/U3157',
    t,
    searchStateHandlers,
    gridStateHandlers,
    runSearch: handleSearch.handleSearch,
    quickSearchData,
    setQuickSearchData,
  });
  consumeQsSessionRef.current = consumeQuickSearchSession;

  if (searchStateHandlers.getColumns().length === 0) {
    return null;
  }

  return (
    <>
      <U3157WorkNatureImportModelDeleteDlg handleDelete={handleDelete} t={t} NS={NS} />
      <U2000BasicTable
        searchStateHandlers={searchStateHandlers}
        gridStateHandlers={gridStateHandlers}
        actionsButtons={
          <U3157WorkNatureImportModelListToolBar gridStateHandlers={gridStateHandlers as never} handlerActions={u3157ListHandlerActions as never} />
        }
        handlerActions={u3157ListHandlerActions}
        label={t('lblImportModelList', {
          count: gridStateHandlers.getTotalRows(),
          ns: NS,
        })}
        allowRowSelection={true}
        hasExportRole={false}
      />
    </>
  );
};

export default U3157WorkNatureImportModelList;
